import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import type { RentalLedgerEntry, RentalLineView } from '../rental-billing.model';
import { getOrderLabel } from '@app/shared/constants';
import { docTypeLabel } from '../rental-billing.labels';
import { frDay, money, qtyFr } from '../rental-days';

export interface LedgerRow {
  key: number; credit: boolean; type: string; ref: string; link: number | null; state: string | null;
  date: string; ranks: string; span: string; qty: string; pu: string; ht: string; ttc: string; lpp: string | null; notes: string[];
}

// Rangs du premier au dernier, les deux inclus.
export function ranksText(rankStart: number, rankEnd: number): string {
  return rankStart === rankEnd ? String(rankStart) : `${rankStart}–${rankEnd}`;
}

// Jours du premier au dernier, les deux inclus.
export function spanText(start: string, end: string): string {
  return `${frDay(start)} – ${frDay(end)}`;
}

// Toute pièce s'ouvre par l'écran des pièces, facture FSE comprise (la pièce de vente qui a porté la FSE), comme en F4.
export function pieceLink(invoiceId: number): number | null {
  return invoiceId > 0 ? invoiceId : null;
}

export function pieceRef(e: { invoice_id: number; doc_type: string | null; doc_ref: string | null }): string {
  return e.doc_ref || `${docTypeLabel(e.doc_type) || 'Pièce'} n° ${e.invoice_id}`;
}

// Reports et corrections cités par une pièce : la ligne de facture d'origine se retrouve par son id dans le registre.
export function carriedTexts(carried: { invoice_line_id: number; day_count: number }[] | null | undefined, ledger: RentalLedgerEntry[]): string[] {
  return (carried ?? []).map((c) => {
    const origin = ledger.find((e) => e.invoice_line_id === c.invoice_line_id);
    return `report ${c.day_count} j depuis ${origin ? pieceRef(origin) : 'la ligne de facture n° ' + c.invoice_line_id}`;
  });
}

export function ledgerRowsOf(line: RentalLineView | null): LedgerRow[] {
  const ledger = line?.ledger ?? [];
  return ledger.map((e) => {
    const notes = carriedTexts(e.carried, ledger);
    if (e.corrects != null) {
      const fixed = ledger.find((x) => x.invoice_line_id === e.corrects);
      notes.push(fixed ? `corrige la ligne de ${pieceRef(fixed)} (rangs ${ranksText(fixed.rank_start, fixed.rank_end)})` : `corrige la ligne de facture n° ${e.corrects}`);
    }
    return {
      key: e.invoice_line_id, credit: e.sign < 0 || e.doc_type === 'AVOIR', type: docTypeLabel(e.doc_type), ref: pieceRef(e), link: pieceLink(e.invoice_id),
      state: e.invoice_state ? getOrderLabel(e.invoice_state) : null, date: frDay(e.invoiced_on), ranks: ranksText(e.rank_start, e.rank_end), span: spanText(e.start, e.end),
      qty: qtyFr(e.qty), pu: money(e.uprice_taxx), ht: money(e.net_taxx), ttc: money(e.net_taxi), lpp: e.lpp ? `LPP ${e.lpp}` : null, notes,
    };
  });
}

// Registre d'une ligne : une rangée par facture ou avoir qui porte data.rental_billing ; présentation seule (E.10).
@Component({
  selector: 'app-rental-line-ledger',
  templateUrl: './rental-line-ledger.component.html',
  styleUrls: ['./rental-line-ledger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTooltipModule, RouterLink],
})
export class RentalLineLedgerComponent implements OnChanges {
  @Input() line: RentalLineView | null = null;

  rows: LedgerRow[] = [];

  ngOnChanges(): void {
    this.rows = ledgerRowsOf(this.line);
  }
}
